import { Component, OnInit } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormControl,
} from "@angular/forms";
import { PtoventaService } from "app/services/ptoventa.service";
import Swal from "sweetalert2";
import { ActivatedRoute, Router } from "@angular/router";
import { PuntoVenta } from "app/models/puntoventa.models";
import { ParametrosService } from "app/services/parametros.service";
import { Provincia } from "app/models/provincias.models";
import { SucursalesService } from "app/services/sucursales.service";
import { AddressAutocompleteService } from "app/services/address-autocomplete.service";
import { Subject } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";

// Interfaz para las predicciones de dirección
interface AddressPrediction {
  description: string;
  place_id: string;
}

@Component({
  selector: "app-create",
  templateUrl: "./create-pto-venta.component.html",
  styleUrls: ["./create-pto-venta.component.css"],
})
export class CreatePtoVentaComponent implements OnInit {
  puntoVenta: PuntoVenta;
  form: UntypedFormGroup;
  lstPuntoVentas = [];
  total = 0;
  provincias: Provincia[] = [];
  idPuntoVenta: number;
  textoBtnSubmit = "Guardar";
  branchs = [];
  
  // Elementos para el autocompletado de direcciones
  searchTerm = new FormControl('');
  private searchSubject = new Subject<string>();
  predictions: AddressPrediction[] = [];
  
  // Propiedades para control de UI
  isLoading = false;
  isSaving = false;
  formReady = false;

  constructor(
    private fb: UntypedFormBuilder,
    private ptoVentaService: PtoventaService,
    private parametroService: ParametrosService,
    private sucursalService: SucursalesService,
    private router: Router,
    private routerActive: ActivatedRoute,
    private addressService: AddressAutocompleteService
  ) {
    this.idPuntoVenta = this.routerActive.snapshot.params.id;
    this.form = this.fb.group({
      number: [0, Validators.required],
      type: ["general"],
      description: [""],
      name: ["", Validators.required],
      electronic: [0],
      street: [""],
      altitude: [""],
      floor: [""],
      email: ["", [Validators.pattern("[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$")]],
      phone: ["", [Validators.pattern("^[0-9]*$")]],
      apartment: [""],
      province: [""],
      locality: [""],
      postalCode: ["", [Validators.pattern("^[0-9]*$")]],
      branchId: [0, Validators.required],
    });
  }

  ngOnInit(): void {
    const sidebar = <HTMLElement>document.querySelector(".navbar-brand");
    let titulo;
    if (this.idPuntoVenta) {
      titulo = "Modificar";
      this.textoBtnSubmit = "Modificar";
      this.getPuntosVentaById();
    } else {
      titulo = "Nuevo";
      this.textoBtnSubmit = "Guardar";
      this.getPuntosVentas();
    }
    if (sidebar !== undefined) {
      sidebar.innerHTML = `${titulo} punto de venta`;
    }
    this.puntoVenta = new PuntoVenta();

    // Mostrar animación de carga
    this.isLoading = true;
    
    // Inicializar datos
    Promise.all([
      this.getProvinciasPromise(),
      this.getSucursalesPromise()
    ]).then(() => {
      // Cuando todo esté cargado
      setTimeout(() => {
        this.isLoading = false;
        this.formReady = true;
      }, 300);
    });
    
    this.setupSearch();
  }
  
  // Configura el buscador de direcciones con autocompletado
  setupSearch() {
    this.searchSubject
      .pipe(
        debounceTime(300),
        switchMap(searchTerm => this.addressService.getSuggestions(searchTerm))
      )
      .subscribe(
        (predictions: AddressPrediction[]) => {
          this.predictions = predictions;
        },
        error => {
          console.error('Error al obtener sugerencias:', error);
        }
      );
  }

  // Método para buscar direcciones
  onSearch() {
    const term = this.searchTerm.value;
    if (term && term.length > 3) {
      this.searchSubject.next(term);
    } else if (!term || term.length === 0) {
      this.predictions = [];
    }
  }

  // Método que se ejecuta cuando se selecciona una dirección
  onAddressSelect(selectedAddress: string) {
    this.searchTerm.setValue(selectedAddress);
    this.predictions = [];
  }

  getPuntosVentas() {
    this.ptoVentaService.getAll().subscribe(
      (data) => {
        this.lstPuntoVentas = data;
        this.total = this.lstPuntoVentas.length + 1;
        this.form.controls.number.setValue(this.total, {
          onlySelf: true,
        });
        // Deshabilitar el campo number para que no pueda ser editado
        this.form.controls.number.disable();
      },
      (err) => console.log(err)
    );
  }

  getPuntosVentaById() {
    this.ptoVentaService.getById(this.idPuntoVenta).subscribe(
      (data) => {
        this.puntoVenta = data;
        // Ajustar los valores nulos a string vacío para evitar problemas en el formulario
        const patch = { ...this.puntoVenta };
        Object.keys(patch).forEach(key => {
          if (patch[key] === null) patch[key] = '';
        });
        // Mapear los campos del backend a los del formulario si hay diferencias
        this.form.patchValue({
          number: patch.number,
          type: patch.type || '',
          description: patch.description || '',
          name: patch.name || '',
          electronic: patch.electronic === 1 ? 1 : 0,
          street: patch.street || '',
          altitude: patch.altitude || '',
          floor: patch.floor || '',
          email: patch.email || '',
          phone: patch.phone || '',
          apartment: patch.apartment || '',
          province: patch.province || '',
          locality: patch.locality || '',
          postalCode: patch.postalCode || '',
          branchId: patch.branchId || '',
        });
        
        // Establecer el valor en el control de búsqueda con la dirección compuesta
        // En base a street, altitude, locality y province
        let direccionCompleta = '';
        if (patch.street) {
          direccionCompleta += patch.street;
          if (patch.altitude) {
            direccionCompleta += ' ' + patch.altitude;
          }
          if (patch.locality || patch.province) {
            direccionCompleta += ', ';
            if (patch.locality) {
              direccionCompleta += patch.locality;
            }
            if (patch.province) {
              direccionCompleta += patch.locality ? ', ' + patch.province : patch.province;
            }
          }
        }
        
        if (direccionCompleta) {
          this.searchTerm.setValue(direccionCompleta);
        }
        
        // Si el campo número debe ser deshabilitado en edición
        this.form.controls.number.disable();
      },
      (err) => console.log(err)
    );
  }

  // Versiones Promise de los métodos de carga
  getProvinciasPromise(): Promise<any> {
    return new Promise((resolve) => {
      this.parametroService.getProvincias().subscribe(
        (r) => {
          this.provincias = r;
          let prov = this.provincias.find((x) => x.code === "19");
          this.form.controls.province.setValue(prov.id);
          resolve(true);
        },
        (err) => {
          console.log(err);
          resolve(false);
        }
      );
    });
  }
  
  getSucursalesPromise(): Promise<any> {
    return new Promise((resolve) => {
      this.sucursalService.getAll().subscribe(
        (data) => {
          this.branchs = data;
          // Si solo hay una sucursal, seleccionarla automáticamente y deshabilitarla
          if (this.branchs && this.branchs.length === 1 && !this.idPuntoVenta) {
            this.form.controls.branchId.setValue(this.branchs[0].id);
            this.form.controls.branchId.disable(); // Deshabilitar para que no se pueda editar
          }
          resolve(true);
        },
        (err) => {
          console.log(err);
          resolve(false);
        }
      );
    });
  }

  async guardar() {
    // Mostrar estado de guardado
    this.isSaving = true;
    
    // Usar getRawValue() para incluir los campos deshabilitados
    this.puntoVenta = this.form.getRawValue();
    
    // Procesar información de la dirección seleccionada y adaptarla a los campos existentes
    if (this.searchTerm.value != '') {
      try {
        const geoData = await this.addressService.getGeocode(this.searchTerm.value);
        
        // Adaptar la información obtenida a los campos disponibles en la BD
        this.puntoVenta.locality = geoData.locality || '';
        this.puntoVenta.province = geoData.province || 'Misiones';
        
        // Extraer la calle y número de la dirección completa 
        const addressParts = this.searchTerm.value.split(',')[0].trim().split(' ');
        if (addressParts.length > 1) {
          // Intentamos separar la calle del número
          const possibleNumber = addressParts[addressParts.length - 1];
          if (!isNaN(Number(possibleNumber))) {
            this.puntoVenta.altitude = possibleNumber;
            this.puntoVenta.street = addressParts.slice(0, -1).join(' ');
          } else {
            this.puntoVenta.street = addressParts.join(' ');
          }
        } else {
          this.puntoVenta.street = this.searchTerm.value;
        }
      } catch (error) {
        console.error('Error obteniendo geocodificación:', error);
        this.puntoVenta.locality = '';
        this.puntoVenta.province = 'Misiones';
        
        // Si hay un error, guardamos la dirección completa en el campo street
        this.puntoVenta.street = this.searchTerm.value;
      }
    } else {
      this.puntoVenta.locality = '';
      this.puntoVenta.province = 'Misiones';
      this.puntoVenta.street = '';
    }
    
    const mostrarMensajeExito = (titulo, texto) => {
      this.isSaving = false;
      
      // Mostrar mensaje de éxito con estilo mejorado
      Swal.fire({
        title: titulo,
        text: texto,
        icon: "success",
        confirmButtonColor: "#333333",
        confirmButtonText: "Aceptar",
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        }
      }).then(() => {
        this.router.navigate(["configurar/punto-venta"]);
      });
    };
    
    const mostrarError = (error) => {
      console.error(error);
      this.isSaving = false;
      Swal.fire({
        title: "Error",
        text: "No se pudo procesar la operación. Por favor intente nuevamente.",
        icon: "error",
        confirmButtonColor: "#F44336",
        confirmButtonText: "Entendido"
      });
    };
    
    //this.puntoVenta.empresa = this.form.value.empresa;
    if (!this.idPuntoVenta) {
      this.ptoVentaService.guardar(this.puntoVenta).subscribe(
        (data) => {
          console.log(data);
          mostrarMensajeExito("¡Excelente!", "Ya has creado un nuevo punto de ventas");
        },
        (err) => mostrarError(err)
      );
    } else {
      this.puntoVenta.id = this.idPuntoVenta;
      delete this.puntoVenta.id;
      this.ptoVentaService.actualizar(this.idPuntoVenta, this.puntoVenta).subscribe(
        (data) => {
          mostrarMensajeExito("¡Excelente!", "El punto de ventas se actualizó correctamente");
        },
        (err) => mostrarError(err)
      );
    }
  }
}
